import type { Locale } from '../site'

const CONSENT_KEY = 'sd.analytics-consent'
const GA_MEASUREMENT_ID = 'G-5NZHN8J5FC'
const BAIDU_SITE_ID = '68f19364e7e1f8c94fff5f83dcf4df45'

type ConsentChoice = 'accepted' | 'rejected'

const copy: Record<Locale, {
  title: string
  body: string
  accept: string
  reject: string
  policy: string
  settings: string
  about: string
  contact: string
  terms: string
}> = {
  en: {
    title: 'Optional analytics',
    body: 'Screen tests run locally. With your permission, anonymous usage analytics help us improve the site.',
    accept: 'Allow analytics',
    reject: 'Necessary only',
    policy: 'Privacy policy',
    settings: 'Cookie settings',
    about: 'About',
    contact: 'Contact',
    terms: 'Terms',
  },
  'zh-CN': {
    title: '可选统计',
    body: '屏幕测试在本地运行。经您同意后，匿名使用统计可帮助我们改进网站。',
    accept: '允许统计',
    reject: '仅必要功能',
    policy: '隐私政策',
    settings: 'Cookie 设置',
    about: '关于',
    contact: '联系',
    terms: '使用条款',
  },
  'zh-TW': {
    title: '可選統計',
    body: '螢幕測試在本機執行。經您同意後，匿名使用統計可協助我們改進網站。',
    accept: '允許統計',
    reject: '僅必要功能',
    policy: '隱私政策',
    settings: 'Cookie 設定',
    about: '關於',
    contact: '聯絡',
    terms: '使用條款',
  },
  ja: {
    title: '任意のアクセス解析',
    body: '画面テストは端末内で実行されます。同意いただいた場合、匿名の利用統計をサイト改善に使用します。',
    accept: '解析を許可',
    reject: '必須機能のみ',
    policy: 'プライバシーポリシー',
    settings: 'Cookie 設定',
    about: '概要',
    contact: 'お問い合わせ',
    terms: '利用規約',
  },
}

export function initPrivacyConsent(locale: Locale): void {
  const text = copy[locale]
  installPrivacyLinks(text)
  // Always boot GA4 with Consent Mode defaults so cookieless pings still show traffic.
  ensureGtagBootstrapped()

  const banner = document.createElement('section')
  banner.className = 'consent-banner'
  banner.setAttribute('aria-label', text.title)
  banner.setAttribute('aria-live', 'polite')
  banner.innerHTML = `
    <div class="consent-banner__copy">
      <strong>${text.title}</strong>
      <p>${text.body} <a href="/privacy.html">${text.policy}</a></p>
    </div>
    <div class="consent-banner__actions">
      <button type="button" class="consent-button consent-button--secondary" data-consent="reject">${text.reject}</button>
      <button type="button" class="consent-button consent-button--primary" data-consent="accept">${text.accept}</button>
    </div>`
  document.body.appendChild(banner)

  const stored = readConsent()
  if (stored === 'accepted') {
    banner.hidden = true
    grantAnalyticsConsent()
    loadBaiduAnalytics()
  } else if (stored === 'rejected') {
    banner.hidden = true
    denyAnalyticsConsent()
  } else {
    denyAnalyticsConsent()
  }

  banner.querySelector('[data-consent="accept"]')?.addEventListener('click', () => {
    saveConsent('accepted')
    banner.hidden = true
    grantAnalyticsConsent()
    loadBaiduAnalytics()
  })
  banner.querySelector('[data-consent="reject"]')?.addEventListener('click', () => {
    const wasAccepted = readConsent() === 'accepted'
    saveConsent('rejected')
    denyAnalyticsConsent()
    clearAnalyticsCookies()
    banner.hidden = true
    if (wasAccepted) location.reload()
  })

  document.querySelectorAll<HTMLElement>('[data-open-consent]').forEach((control) => {
    control.addEventListener('click', (event) => {
      event.preventDefault()
      banner.hidden = false
      banner.querySelector<HTMLButtonElement>('button')?.focus()
    })
  })
}

function installPrivacyLinks(text: (typeof copy)[Locale]): void {
  document.querySelectorAll<HTMLElement>('.app-footer').forEach((footer) => {
    if (footer.querySelector('[data-privacy-links]')) return
    const items = [
      { href: '/about.html', label: text.about },
      { href: '/contact.html', label: text.contact },
      { href: '/terms.html', label: text.terms },
      { href: '/privacy.html', label: text.policy },
      { href: '/privacy.html#cookie-settings', label: text.settings, consent: true },
    ].filter(({ href }) => !footer.querySelector(`a[href="${href}"]`))
    if (!items.length) return

    const links = document.createElement('span')
    links.dataset.privacyLinks = ''
    links.innerHTML = items
      .map(({ href, label, consent }) => `
        <span aria-hidden="true"> · </span>
        <a href="${href}"${consent ? ' data-open-consent' : ''}>${label}</a>`)
      .join('')
    footer.appendChild(links)
  })
}

function readConsent(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY)
    return value === 'accepted' || value === 'rejected' ? value : null
  } catch {
    return null
  }
}

function saveConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice)
  } catch {
    // A blocked storage API means the choice applies only to this page load.
  }
}

function gtag(...args: unknown[]): void {
  const dataLayer = ((window as typeof window & { dataLayer?: unknown[] }).dataLayer ??= [])
  dataLayer.push(args)
}

function ensureGtagBootstrapped(): void {
  if (document.querySelector('[data-screen-detect-analytics]')) return

  const marker = document.createElement('meta')
  marker.dataset.screenDetectAnalytics = ''
  document.head.appendChild(marker)

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  })
  gtag('js', new Date())
  gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: true,
  })

  const google = document.createElement('script')
  google.async = true
  google.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(google)
}

function grantAnalyticsConsent(): void {
  gtag('consent', 'update', {
    analytics_storage: 'granted',
  })
}

function denyAnalyticsConsent(): void {
  gtag('consent', 'update', {
    analytics_storage: 'denied',
  })
}

function loadBaiduAnalytics(): void {
  if (document.querySelector('[data-screen-detect-baidu]')) return
  const marker = document.createElement('meta')
  marker.dataset.screenDetectBaidu = ''
  document.head.appendChild(marker)

  const baidu = document.createElement('script')
  ;((window as typeof window & { _hmt?: unknown[] })._hmt ??= [])
  baidu.async = true
  baidu.src = `https://hm.baidu.com/hm.js?${BAIDU_SITE_ID}`
  document.head.appendChild(baidu)
}

function clearAnalyticsCookies(): void {
  document.cookie.split(';').forEach((entry) => {
    const name = entry.split('=')[0]?.trim()
    const isAnalyticsCookie =
      name?.startsWith('_ga') ||
      name?.startsWith('_gid') ||
      name?.startsWith('_gat') ||
      name?.startsWith('_hmt') ||
      name?.startsWith('Hm_') ||
      name === 'HMACCOUNT'
    if (!name || !isAnalyticsCookie) return
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.${location.hostname}; SameSite=Lax`
  })
}
